import { signOut } from 'flow/actions/app-actions';
import { releaseCompanionRoleOnLogout } from 'flow/companion/companion-actions';
import { createIntegrationAccessToken } from 'flow/actions/integration-access-actions';
import { useAuthService } from 'flow/authservice/useAuthService';
import { getBrand } from 'flow/brands/brands';
import { selectAutoLogin } from 'flow/selectors/app-selectors';
import { allowCtiCommunication } from 'flow/selectors/cti-integration-selectors';
import { selectRetailerKey } from 'flow/selectors/customer-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectIsAdmin,
    selectHasTranscriptionCapabilityBusinessOrPersonal,
} from 'flow/selectors/own-extension-selectors';
import { CreatedToken } from 'flow/types/IntegrationAccess';
import { NavigationConfiguration } from 'flow/types/Navigation';
import { isEmbeddedTypeCti } from 'flow/utils/app-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { hasCtiSettings } from 'flow/utils/cti-settings-utils';
import { buildStatsSignInUrl } from 'flow/utils/stats-utils';
import isElectron from 'is-electron';
import { parse } from 'tldts';
import useAppContext from './useAppContext';
import { useAppDispatch, useAppSelector } from './useRedux';
import { useUserRegionData } from './region-lookup/useUserRegionData';

const STATISTICS_DOMAIN_KEY = 'integrationaccessdomain-9';
const STATISTICS_TOKEN_EXPIRATION_30_DAY_IN_MINUTES = 60 * 24 * 30;
const ipcRenderer = isElectron() ? require('electron').ipcRenderer : null;

const useNavigationMenu = () => {
    const { embedded } = useAppContext();
    const dispatch = useAppDispatch();
    const { authLogout, isAuthenticated, isAuthServiceEnabled } =
        useAuthService();
    const { clearUserData, isRegionCheckEnabled } = useUserRegionData();

    const autoLoginEnabled = useAppSelector(selectAutoLogin);
    const ctiSettingExist = useAppSelector((state) =>
        allowCtiCommunication(state) ? hasCtiSettings(state) : false,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const retailerKey = useAppSelector((state) =>
        selectRetailerKey(state, ownExtensionKey),
    );
    const isAdmin = useAppSelector(selectIsAdmin);
    const hasTranscriptionBusinessOrPersonal = useAppSelector(
        selectHasTranscriptionCapabilityBusinessOrPersonal,
    );
    const hasWallboardAccess = useAppSelector((state) =>
        state.wallboards.get('hasAccess'),
    );

    const isUaeNonAdminUser = IS_UAE_HOST && !isAdmin;

    const config: NavigationConfiguration = {
        shouldShowWallboard: hasWallboardAccess,
        shouldShowCti: isEmbeddedTypeCti(embedded) && ctiSettingExist,
        shouldShowMobile:
            !IS_UAE_HOST && !['retailer-1211'].includes(retailerKey),
        shouldShowAdminPortal:
            !isEmbeddedTypeCti(embedded) && !isUaeNonAdminUser,
        shouldShowStats: !!getBrand().brandStatsApplicationUrl && isAdmin,
        shouldShowLogOut: !autoLoginEnabled,
        shouldShowTranscriptions: hasTranscriptionBusinessOrPersonal,
    };

    const proxyHost = process.env.PROXY_HOST;
    const adminPortalUrl: string | undefined = proxyHost
        ? `http://${proxyHost}`
        : getBrand().brandAdminUrl;

    const navigateToAdminPortal = () => {
        if (!adminPortalUrl) {
            console.warn(
                'Brand admin URL is not available, navigation blocked',
            );
            return;
        }
        window.open(adminPortalUrl);
    };

    const createCookieAndGoToStats = (data: CreatedToken) => {
        const url = getBrand().brandStatsApplicationUrl;

        if (!url) {
            console.warn(
                'Brand stats URL is not available, navigation blocked',
            );
            return;
        }

        const parsedUrl = parse(url);
        const domain = `.${parsedUrl.domain}`;

        if (isElectron()) {
            ipcRenderer.send('open-stats-window-with-cookie', {
                url,
                cookie: {
                    url,
                    name: 'auth_token',
                    value: encodeURIComponent(data.accessToken),
                    path: '/',
                    domain,
                    secure: true,
                    maxAge: 60 * STATISTICS_TOKEN_EXPIRATION_30_DAY_IN_MINUTES,
                },
            });
        } else {
            document.cookie = `auth_token=${encodeURIComponent(data.accessToken)}; path=/; max-age=300; domain=${domain}; Secure;`;
            window.open(buildStatsSignInUrl(url, data.accessToken), '_blank');
        }
    };

    const goToStatsInsights = () =>
        dispatch(
            createIntegrationAccessToken(
                ownExtensionKey,
                STATISTICS_DOMAIN_KEY,
                'Statistics',
                STATISTICS_TOKEN_EXPIRATION_30_DAY_IN_MINUTES,
                createCookieAndGoToStats,
            ),
        );

    const authServiceLogout = async () => {
        if (isAuthenticated && isAuthServiceEnabled) {
            await authLogout();
        }
    };

    const logOut = async (path: string) => {
        // Before the auth call, not inside signOut: releasing needs a session, and by the time
        // signOut runs there isn't one. A desktop app that stays designated after its user logs
        // out leaves every surface relaying calls to a machine nobody is signed into, and nothing
        // reports the failure back - the calls simply stop arriving. The involuntary paths into
        // signOut (an expired session, a region mismatch) cannot do this and still fall back to
        // the staleness check.
        await dispatch(releaseCompanionRoleOnLogout());
        await authServiceLogout();
        if (isRegionCheckEnabled) {
            clearUserData();
        }
        dispatch(signOut(path));
    };

    return {
        config,
        adminPortalUrl,
        navigateToAdminPortal,
        goToStatsInsights,
        logOut,
    };
};

export default useNavigationMenu;
